/**
 * Даты работы места: выставки, сезонные площадки, временные события.
 *
 * Поля `startDate` / `endDate` приходят из Strapi строкой 'YYYY-MM-DD'.
 * Обе необязательны. Сравниваем строками, а не через `new Date()`:
 * `new Date('2026-11-30')` — это полночь по UTC, то есть 03:00 по Петербургу,
 * и на телефоне с другим поясом дата съезжала бы на соседний день.
 *
 * «Сегодня» берётся по Петербургу — так же считает бэкенд, когда убирает
 * закончившиеся места из лент (`notEndedFilter` в контроллере place).
 */

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** Сегодня по Петербургу, 'YYYY-MM-DD'. en-CA отдаёт дату ровно как ISO */
export const todayInSpb = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Moscow',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date());

const parse = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.match(DATE_RE);
  if (!match) return null;
  const [, y, m, d] = match;
  const month = Number(m);
  if (month < 1 || month > 12) return null;
  return { iso: `${y}-${m}-${d}`, year: Number(y), month, day: Number(d) };
};

// Год пишем, только если он не текущий: «до 30 ноября», но «до 15 января 2027»
const formatDay = (date, currentYear) => {
  const base = `${date.day} ${MONTHS_GENITIVE[date.month - 1]}`;
  return date.year === currentYear ? base : `${base} ${date.year}`;
};

const formatRange = (start, end, currentYear) => {
  // Один месяц одного года — месяц не повторяем: «1 — 30 ноября»
  if (start.year === end.year && start.month === end.month) {
    return `${start.day} — ${formatDay(end, currentYear)}`;
  }
  // Разные годы — год нужен у обеих дат, иначе «20 декабря — 10 января 2027»
  // читается как два декабря подряд
  if (start.year !== end.year) {
    return `${start.day} ${MONTHS_GENITIVE[start.month - 1]} ${start.year} — ${formatDay(end, -1)}`;
  }
  return `${formatDay(start, currentYear)} — ${formatDay(end, currentYear)}`;
};

/**
 * @param {{startDate?: string|null, endDate?: string|null}} place
 * @param {string} [today] - 'YYYY-MM-DD', для тестов
 * @returns {null | {
 *   status: 'upcoming'|'running'|'ended',
 *   short: string|null,  // для карточки: «до 30 ноября», null — ничего не показывать
 *   full: string,        // для страницы места: «1 сентября — 30 ноября»
 * }}
 */
export const getPlaceDates = (place, today = todayInSpb()) => {
  const start = parse(place?.startDate);
  let end = parse(place?.endDate);
  if (!start && !end) return null;

  // Конец раньше начала — ошибка ввода в админке. Показываем то, в чём
  // уверены, а не «до 1 сентября» у выставки, которая откроется в декабре
  if (start && end && end.iso < start.iso) end = null;

  const currentYear = Number(today.slice(0, 4));

  if (end && end.iso < today) {
    return { status: 'ended', short: null, full: `Закончилось ${formatDay(end, currentYear)}` };
  }

  if (start && start.iso > today) {
    return {
      status: 'upcoming',
      short: `с ${formatDay(start, currentYear)}`,
      full: end ? formatRange(start, end, currentYear) : `с ${formatDay(start, currentYear)}`,
    };
  }

  if (end) {
    return {
      status: 'running',
      short: `до ${formatDay(end, currentYear)}`,
      full: start ? formatRange(start, end, currentYear) : `до ${formatDay(end, currentYear)}`,
    };
  }

  // Только дата начала, и она уже прошла: место просто работает.
  // На карточке это ничего не сообщает, на странице — оставляем
  return { status: 'running', short: null, full: `с ${formatDay(start, currentYear)}` };
};

export default getPlaceDates;
